// Les réactions — le seul endroit qui dise lesquelles existent.
//
// Trois écrans s'en servent (publications, messages, statuts) et deux pilotes
// de données les valident. Une liste recopiée à cinq endroits finit toujours
// par diverger : celle-ci ne vit qu'ici, et tout le reste la lit.
//
// ⚠️ ICI, ET SEULEMENT ICI, DES ÉMOJIS.
//
// Ailleurs — les pastilles de notification, la barre du bas, les boutons — ce
// sont nos propres tracés, parce qu'un émoji ne se dessine pas pareil d'un
// appareil à l'autre et qu'aucun ne ressemble à Koinonia. Une réaction, c'est
// autre chose : elle doit se reconnaître AU DIXIÈME DE SECONDE, en couleur, et
// personne n'a besoin d'apprendre à lire 👍 ou 😢. Un tracé blanc sur un disque
// de couleur, à 17 px, ne dit pas la moitié de ce que dit l'émoji.
//
// Ce fut d'ailleurs éprouvé dans l'autre sens : les sept réactions ont d'abord
// été dessinées à la main, et à la taille où elles s'affichent, « Haha » et
// « Wouah » se ressemblaient trop.
//
// L'AMEN EST EN TÊTE, ET IL Y RESTE. C'est le geste du réseau : il existait
// avant les six autres, il compte déjà dans la base, et un appui simple
// continue de le poser sans ouvrir aucune palette. Les six de Facebook
// s'ajoutent à lui, elles ne le remplacent pas.

/* `couleur` : la pastille de la NOTIFICATION prend la couleur de la réaction
   reçue — demandé par Josaphat le 24 octobre 2026 (le soutien en bleu, et
   chaque réaction sa teinte). Le téléphone garde une copie de ces teintes
   dans `ecrans/Notifications.jsx` : qui change ici change là-bas. */
export const REACTIONS = [
  { cle: "amen", label: "Amen", emoji: "🙏", couleur: "#1877f2" },
  { cle: "jaime", label: "J'aime", emoji: "👍", couleur: "#1877f2" },
  { cle: "jadore", label: "J'adore", emoji: "❤️", couleur: "#e0245e" },
  { cle: "haha", label: "Haha", emoji: "😆", couleur: "#c9871a" },
  { cle: "wouah", label: "Wouah", emoji: "😮", couleur: "#c9871a" },
  { cle: "triste", label: "Triste", emoji: "😢", couleur: "#0e93a4" },
  { cle: "colere", label: "En colère", emoji: "😡", couleur: "#e8590c" },
];

export const CLE_DEFAUT = "amen";

const PAR_CLE = new Map(REACTIONS.map((r) => [r.cle, r]));

/** La réaction nommée, ou l'Amen — jamais `undefined` : un écran ne doit pas
    avoir à se demander quoi dessiner. */
export const reaction = (cle) => PAR_CLE.get(cle) || PAR_CLE.get(CLE_DEFAUT);

/**
 * Une clé venue de la base ou d'un clic est-elle une vraie réaction ?
 *
 * Rend `null` sinon. Les pilotes s'en servent avant d'écrire : une clé inventée
 * ne doit pas se glisser dans la base, où elle apparaîtrait ensuite comme une
 * colonne vide dans tous les résumés.
 */
export const reactionSure = (v) => (PAR_CLE.has(v) ? v : null);

/**
 * Résume une carte { uid: clé } en ce qu'il faut afficher.
 *
 * Rend les réactions PRÉSENTES, les plus nombreuses d'abord, plus le total et
 * la mienne. Trois au maximum sont montrées — au-delà, la rangée de pastilles
 * devient plus large que le texte qu'elle commente, et c'est le nombre qui
 * porte l'information, pas la collection.
 */
export function resumer(carte, moi) {
  const compte = new Map();
  let total = 0;
  for (const [uid, cle] of Object.entries(carte || {})) {
    if (!PAR_CLE.has(cle)) continue; // une clé d'une version future, ignorée
    compte.set(cle, (compte.get(cle) || 0) + 1);
    total++;
    void uid;
  }
  const tetes = [...compte.entries()]
    .sort((a, b) => b[1] - a[1] || REACTIONS.findIndex((r) => r.cle === a[0]) - REACTIONS.findIndex((r) => r.cle === b[0]))
    .slice(0, 3)
    .map(([cle]) => cle);
  return { total, tetes, mienne: reactionSure((carte || {})[moi]) };
}

/**
 * Le même résumé, mais pour une publication.
 *
 * Les publications ne portent pas une carte : elles portent des COMPTEURS, un
 * par réaction (`p.reactions = { amen: 12, jaime: 3 }`). C'est ce qui permet à
 * un fil de trente publications d'afficher ses totaux sans lire, pour chacune,
 * la liste de ceux qui ont réagi.
 *
 * ⚠️ `p.amens` reste le TOTAL, et reste la source pour tout ce qui a été
 * publié avant les réactions : ces publications-là n'ont pas de carte, et leur
 * compte d'Amens serait perdu si on ne se rabattait pas dessus.
 */
export function resumerCompteurs(p, mienne) {
  const compteurs = (p && p.reactions) || {};
  const detail = REACTIONS.map((r) => [r.cle, Number(compteurs[r.cle]) || 0]).filter(([, n]) => n > 0);
  const totalDetaille = detail.reduce((s, [, n]) => s + n, 0);
  // Le total fait foi côté publication : il porte aussi les Amens d'avant.
  const total = Math.max(Number(p && p.amens) || 0, totalDetaille);
  const tetes = detail.length
    ? detail.sort((a, b) => b[1] - a[1]).slice(0, 3).map(([cle]) => cle)
    : total > 0
      ? [CLE_DEFAUT] // une publication d'avant les réactions : ce sont des Amens
      : [];
  return { total, tetes, mienne: reactionSure(mienne) };
}
